import { ID } from '../../domain/types/common';
import { Folder } from '../../domain/models/folder';
import { IPromptRepository } from '../../domain/repositories/promptRepository';
import {
  IFolderRepository,
  IFolderHierarchyBuilder,
  ISpecialFolderFactory,
  FolderHierarchyNode,
} from '../../domain/repositories/folderRepository';
import { ChromePromptRepository } from './chromePromptRepository';
import { ChromeFolderRepository } from './chromeFolderRepository';
import { DomainEventPublisher } from '../../domain/events/domainEvents';

export class FolderHierarchyBuilder implements IFolderHierarchyBuilder {
  buildHierarchy(folders: Folder[]): FolderHierarchyNode[] {
    const folderMap = new Map<ID, Folder>();
    const nodeMap = new Map<ID, FolderHierarchyNode>();
    const rootNodes: FolderHierarchyNode[] = [];

    // Create folder map
    folders.forEach(folder => {
      folderMap.set(folder.id, folder);
    });

    // Create nodes and build paths
    folders.forEach(folder => {
      const path: Folder[] = [];
      let currentId: ID | undefined = folder.id;

      while (currentId) {
        const currentFolder = folderMap.get(currentId);
        if (!currentFolder) break;
        path.unshift(currentFolder);
        currentId = currentFolder.parentId;
      }

      const node: FolderHierarchyNode = {
        folder,
        children: [],
        level: path.length - 1,
        path,
      };

      nodeMap.set(folder.id, node);
    });

    // Build tree
    nodeMap.forEach(node => {
      if (node.folder.parentId) {
        const parentNode = nodeMap.get(node.folder.parentId);
        if (parentNode) {
          parentNode.children.push(node);
        }
      } else {
        rootNodes.push(node);
      }
    });

    return rootNodes;
  }

  validateMove(hierarchy: FolderHierarchyNode[], sourceId: ID, targetId: ID | null): boolean {
    const findNode = (nodes: FolderHierarchyNode[], id: ID): FolderHierarchyNode | null => {
      for (const node of nodes) {
        if (node.folder.id === id) return node;
        const found = findNode(node.children, id);
        if (found) return found;
      }
      return null;
    };

    const isDescendant = (node: FolderHierarchyNode | null, targetId: ID): boolean => {
      if (!node) return false;
      if (node.folder.id === targetId) return true;
      return node.children.some(child => isDescendant(child, targetId));
    };

    const sourceNode = findNode(hierarchy, sourceId);
    return targetId === null || (sourceNode !== null && !isDescendant(sourceNode, targetId));
  }

  findNodeById(hierarchy: FolderHierarchyNode[], folderId: ID): FolderHierarchyNode | null {
    for (const node of hierarchy) {
      if (node.folder.id === folderId) return node;
      const found = this.findNodeById(node.children, folderId);
      if (found) return found;
    }
    return null;
  }

  getPath(hierarchy: FolderHierarchyNode[], folderId: ID): Folder[] {
    const node = this.findNodeById(hierarchy, folderId);
    return node ? node.path : [];
  }
}

export class SpecialFolderFactory implements ISpecialFolderFactory {
  createAllPromptsFolder(): Folder {
    return Folder.fromProps({
      id: 'all-prompts',
      name: 'All Prompts',
      color: '#4A5568',
      type: 'special',
      order: 0,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  }

  createFavoritesFolder(): Folder {
    return Folder.fromProps({
      id: 'favorites',
      name: 'Favorites',
      color: '#ED8936',
      type: 'special',
      order: 1,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  }

  createRecentlyUsedFolder(): Folder {
    return Folder.fromProps({
      id: 'recently-used',
      name: 'Recently Used',
      color: '#48BB78',
      type: 'special',
      order: 2,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  }

  createArchivedFolder(): Folder {
    return Folder.fromProps({
      id: 'archived',
      name: 'Archived',
      color: '#718096',
      type: 'special',
      order: 3,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  }

  getSpecialFolderIds(): string[] {
    return ['all-prompts', 'favorites', 'recently-used', 'archived'];
  }

  isSpecialFolder(id: ID): boolean {
    return this.getSpecialFolderIds().includes(id);
  }
}

export class RepositoryFactory {
  private static instance: RepositoryFactory;
  private promptRepository: IPromptRepository;
  private folderRepository: IFolderRepository;

  private constructor(
    private readonly eventPublisher: DomainEventPublisher,
    private readonly hierarchyBuilder: IFolderHierarchyBuilder,
    private readonly specialFolderFactory: ISpecialFolderFactory
  ) {
    this.promptRepository = new ChromePromptRepository();
    this.folderRepository = new ChromeFolderRepository();
  }

  static initialize(
    eventPublisher: DomainEventPublisher,
    hierarchyBuilder?: IFolderHierarchyBuilder,
    specialFolderFactory?: ISpecialFolderFactory
  ): RepositoryFactory {
    if (!RepositoryFactory.instance) {
      RepositoryFactory.instance = new RepositoryFactory(
        eventPublisher,
        hierarchyBuilder ?? new FolderHierarchyBuilder(),
        specialFolderFactory ?? new SpecialFolderFactory()
      );
    }
    return RepositoryFactory.instance;
  }

  static getInstance(): RepositoryFactory {
    if (!RepositoryFactory.instance) {
      throw new Error('RepositoryFactory not initialized');
    }
    return RepositoryFactory.instance;
  }

  getPromptRepository(): IPromptRepository {
    return this.promptRepository;
  }

  getFolderRepository(): IFolderRepository {
    return this.folderRepository;
  }

  getHierarchyBuilder(): IFolderHierarchyBuilder {
    return this.hierarchyBuilder;
  }

  getSpecialFolderFactory(): ISpecialFolderFactory {
    return this.specialFolderFactory;
  }

  getEventPublisher(): DomainEventPublisher {
    return this.eventPublisher;
  }
}
